import { differenceInDays, isToday } from 'date-fns'
import { ChartFreshness, IFact, IFactSource, IKpiItem } from 'shared/interfaces'
import { filterFactSources } from 'shared/utils/factSources'

export const kpiFromFacts = (
  facts: IFact[],
  factSources: IFactSource[],
  countryCode: string,
  projectConfiguration: string
): IKpiItem[] => {
  const sortedFacts = [...facts].sort((fact1, fact2) => {
    const date1 = Date.parse(fact1.position_current_at)
    const date2 = Date.parse(fact2.position_current_at)

    return date1 < date2 ? 1 : -1
  })

  return filterFactSources(factSources, countryCode).reduce((result: IKpiItem[], source) => {
    const isWrongConfig =
      projectConfiguration !== 'ep' ? source.configuration !== projectConfiguration : source.configuration !== 'album'

    if (isWrongConfig) return result

    const fact = sortedFacts.find((fact) => fact.chart === source.chart)

    return [
      ...result,
      {
        source: {
          chart: source.chart,
          cycle: source?.cycle || '',
          release_info: source?.release_info || '',
          limit: source?.import_limit || ''
        },
        fact
      }
    ]
  }, [])
}

export const isOut = (kpiItem: IKpiItem, latestChart: ChartFreshness): boolean => {
  if (!kpiItem || !latestChart) return false

  const { source, fact } = kpiItem
  if (!source || !fact) return false

  const latestChartDate = Date.parse(latestChart.date)
  const latestFactDate = Date.parse(fact.position_current_at)

  // in days, AM is
  const dailyChartThreshold: number = fact.chart.startsWith('applemusic') ? 1 : 2

  const isChartLate = differenceInDays(Date.now(), latestChartDate) > dailyChartThreshold

  switch (source.cycle) {
    case 'live':
      return !isToday(latestFactDate)
    case 'daily':
      if (isChartLate && differenceInDays(Date.now(), latestFactDate) < 2 * dailyChartThreshold) {
        return false
      }
      return differenceInDays(Date.now(), latestFactDate) > dailyChartThreshold
    case 'weekly':
      return differenceInDays(Date.now(), latestFactDate) > 7
  }
  return false
}

export const previousFact = (facts: IFact[], chart: string): IFact | undefined => {
  const lastFact = facts?.find((fact) => fact.chart === chart)

  if (!lastFact) return

  return facts.find((fact) => fact.chart === chart && fact.id !== lastFact.id)
}

export const peakFact = (facts: IFact[], chart: string): IFact | undefined =>
  [...facts]
    .sort((fact1, fact2) => {
      const date1 = Date.parse(fact1.position_current_at)
      const date2 = Date.parse(fact2.position_current_at)
      const position1 = fact1.position_current
      const position2 = fact2.position_current

      if (position1 === position2) {
        return date1 < date2 ? 1 : date1 > date2 ? -1 : 0
      } else return position1 > position2 ? 1 : -1
    })
    .find((fact) => fact.chart === chart)

export const latestChartForFactSource = (
  chartName: string,
  configuration: string,
  latestCharts: ChartFreshness[]
): ChartFreshness => {
  const latestChart = latestCharts.filter((chartFreshness) => {
    return (
      chartFreshness.configuration.toLowerCase() === configuration.toLowerCase() && chartFreshness.chart === chartName
    )
  })

  return latestChart[0]
}
